/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
	Box,
	Button,
	Center,
	Checkbox,
	Container,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useDisclosure,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
	useToast,
	useBreakpointValue,
} from '@chakra-ui/react';
import { faFileExcel, faPlus, faPrint, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import { useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import PrintLayout from '../../components/Print/PrintLayout';
import LoadingResponsive from '../../components/Table/LoadingResponsive';
import datePickerFromToToDate from '../../utils/datePickerFromToToDate';
import useStore from '../../store';
import moment from 'moment';
import c from '../../constant';
import TrainingRecordAdd from './add';
import TrainingRecordExport from './export';
import SmallDownloadAndViewButton from '../../components/Files/SmallDownloadAndViewButton';
import TrainingCourseFilter from '../../components/Filter/TrainingCourseFilter';
import { ButtonDropdown } from '@cloudscape-design/components';

const QUERY_DATA = gql`
	query get(
		$limit: Float
		$offset: Float
		$no: Float
		$officials: String
		$type: TrainingRecordEnumType
		$means: TrainingRecordEnumMeans
		$location: String
		$locationType: TrainingRecordEnumLocationType
		$dateStart: DateTime
		$dateEnd: DateTime
		$organiser: String
		$courseTitle: String
		$department: String
		$office: String
		$bioListing: Boolean
	) {
		getTrainingRecord(
			pagerInput: { limit: $limit, offset: $offset }
			no: $no
			officials: $officials
			type: $type
			means: $means
			location: $location
			locationType: $locationType
			dateStart: $dateStart
			dateEnd: $dateEnd
			organiser: $organiser
			courseTitle: $courseTitle
			bioListing: $bioListing
			department: $department
			office: $office
		) {
			records {
				id
				no
				courseTitle
				organiser
				dateStart
				dateEnd
				location
				locationType
				means
				type
				attachmentFile
				attachmentFileSize
				bioListing
				user {
					id
					username
					fullName
					fullNameKhmer
					department
					position
				}
				notification {
					id
					read
				}
			}
			totalPages
			totalRows
		}
	}
`;

const Q_DELETE_TRAINING_COURSE = gql`
	mutation RemoveTrainingCourse($id: String!) {
		removeTrainingCourse(id: $id)
	}
`;

const MUTATION_READ_NOTIFICATION = gql`
	mutation read($id: String!) {
		readNotificationById(id: $id)
	}
`;

function TrainingRecordList() {
	const location = useLocation();
	const history = useHistory();
	const toast = useToast();
	const urlParams = new URLSearchParams(location.search);
	const currentPage = urlParams.get('page') ? urlParams.get('page') : 1;
	const officials = urlParams.get('officials') ? urlParams.get('officials') : null;
	const officialName = urlParams.get('officialName') ? urlParams.get('officialName') : '';
	const officialNameKhmer = urlParams.get('officialNameKhmer') ? urlParams.get('officialNameKhmer') : '';
	const officialAvatar = urlParams.get('officialAvatar') ? urlParams.get('officialAvatar') : '';
	const no = urlParams.get('no') ? urlParams.get('no') : null;
	const means = urlParams.get('means') ? urlParams.get('means') : null;
	const type = urlParams.get('type') ? urlParams.get('type') : null;
	const dateStart = urlParams.get('dateStart') ? urlParams.get('dateStart') : null;
	const dateEnd = urlParams.get('dateEnd') ? urlParams.get('dateEnd') : null;
	const courseTitle = urlParams.get('courseTitle') ? urlParams.get('courseTitle') : null;
	const organiser = urlParams.get('organiser') ? urlParams.get('organiser') : null;
	const trainingLocation = urlParams.get('location') ? urlParams.get('location') : null;
	const locationType = urlParams.get('locationType') ? urlParams.get('locationType') : null;
	const bioListing = urlParams.get('bioListing') ? urlParams.get('bioListing') : null;
	const department = urlParams.get('department') ? urlParams.get('department') : null;
	const office = urlParams.get('office') ? urlParams.get('office') : null;
	const read = urlParams.get('read') ? urlParams.get('read') : null;
	const modalFilter = useDisclosure();
	const modalExport = useDisclosure();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { countUnreadNotifications } = useStore((state) => state.countUnreadNotifications);
	const setCountUnreadNotifications = useStore((state) => state.setCountUnreadNotifications);
	const [rowLimit, setRowLimit] = useState(10);
	const [record, setRecord] = useState([]);
	const [exportRecords, setExportRecords] = useState([]);
	const [readNotification, {}] = useMutation(MUTATION_READ_NOTIFICATION);
	const [deletedId, setDeletedId] = useState(null);
	const [isOpeningDeleteDialog, setOpeningDeleteDialog] = useState(false);
	const deleteDialogRef = useRef();
	const deleteDialogCancelRef = useRef();
	const showSmallScreen = useBreakpointValue({
		base: true,
		sm: true,
		md: false,
		lg: false, // >= 992px
		xl: false, // >= 1280px
	});
	const [removeTrainingCourse, { loading: loadingRemoveTrainingCourse, data: dataRemoveTrainingCourse, error: errorRemoveTrainingCourse }] =
		useMutation(Q_DELETE_TRAINING_COURSE);
	const [loadData, { loading, data }] = useLazyQuery(QUERY_DATA, { fetchPolicy: 'network-only' });

	moment.locale(lang === 'kh' ? 'km' : 'en-gb');

	function callbackFilter(e) {
		history.push(`/training-course${e}`);
	}

	const getVariables = () => ({
		offset: currentPage ? (currentPage - 1) * rowLimit : 0,
		limit: rowLimit,
		officials: officials || null,
		no: no ? parseFloat(no) : null,
		type: type || null,
		means: means || null,
		location: trainingLocation || null,
		locationType: locationType || null,
		organiser: organiser || null,
		courseTitle: courseTitle || null,
		department: department || null,
		office: office || null,
		dateStart: dateStart ? datePickerFromToToDate(dateStart) : null,
		dateEnd: dateEnd ? datePickerFromToToDate(dateEnd) : null,
		bioListing: bioListing === 'true' ? true : null,
	});

	useEffect(() => {
		if (currentPage) {
			window.scrollTo(0, 0);
			loadData({
				variables: getVariables(),
			});
		}
	}, [
		rowLimit,
		currentPage,
		no,
		officials,
		type,
		means,
		dateStart,
		dateEnd,
		read,
		courseTitle,
		organiser,
		trainingLocation,
		locationType,
		bioListing,
		department,
		office,
	]);

	useEffect(() => {
		if (data) {
			setRecord(data?.getTrainingRecord?.records);
		}
	}, [data]);

	async function openTrainingRecord(item) {
		if (item?.notification?.id && item?.notification?.read === false) {
			await readNotification({
				variables: {
					id: item?.notification?.id,
				},
			});

			setCountUnreadNotifications(Math.max(countUnreadNotifications - 1, 0));
		}
	}

	function handlePageClick(data) {
		let selected = data.selected;
		const page = selected + 1;

		let url = `?page=${page}`;

		if (officials) {
			url += `&officials=${encodeURIComponent(officials)}`;
		}

		if (courseTitle) {
			url += `&courseTitle=${encodeURIComponent(courseTitle)}`;
		}

		if (officialName) {
			url += `&officialName=${encodeURIComponent(officialName)}`;
		}

		if (officialNameKhmer) {
			url += `&officialNameKhmer=${encodeURIComponent(officialNameKhmer)}`;
		}

		if (officialAvatar) {
			url += `&officialAvatar=${encodeURIComponent(officialAvatar)}`;
		}

		if (no) {
			url += `&no=${no}`;
		}

		if (type) {
			url += `&type=${type}`;
		}

		if (means) {
			url += `&means=${means}`;
		}

		if (department) {
			url += `&department=${department}`;
		}

		if (organiser) {
			url += `&organiser=${organiser}`;
		}

		if (dateStart) {
			url += `&dateStart=${dateStart}`;
		}

		if (dateEnd) {
			url += `&dateEnd=${dateEnd}`;
		}

		if (bioListing) {
			url += `&bioListing=${bioListing}`;
		}

		if (read !== null) {
			const unreadOnly = read ? read : '';
			if (unreadOnly) {
				url += `&read=false`;
			}
		}

		history.push(`/training-course${url}`);
	}

	function onRejectDeleteDialog() {
		setOpeningDeleteDialog(false);
		setDeletedId(null);
	}

	async function onApproveDeleteDialog() {
		if (!deletedId) return;

		await removeTrainingCourse({
			variables: { id: deletedId },
		});

		setOpeningDeleteDialog(false);
		setDeletedId(null);

		toast({
			title: t('Training Record has been removed'),
			status: 'success',
			isClosable: true,
			position: 'top-right',
		});

		loadData({
			variables: getVariables(),
		});
	}

	function actionMenu(row) {
		return (
			<Box onClick={(e) => e.stopPropagation()}>
				<Flex justifyContent="flex-end" alignItems="center">
					<ButtonDropdown
						className="action-button-table"
						variant="inline-icon"
						ariaLabel={t('Actions')}
						expandToViewport={true}
						items={[{ id: 'delete', text: <p style={{ color: 'red' }}>{t('Delete')}</p> }]}
						onItemClick={({ detail }) => {
							switch (detail?.id) {
								case 'delete':
									setDeletedId(row?.id);
									setOpeningDeleteDialog(true);
									break;
								default:
									break;
							}
						}}
					/>
				</Flex>
			</Box>
		);
	}

	const canShowCreateButton = (user?.position !== '1' && user?.role === 'admin') || user?.canSubmitTrainingCourse === true;

	return (
		<PrintLayout>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'Training Course',
							path: '#',
						},
					]}
				/>
				<Center className="show-only-printing">
					<Text textAlign="center" color="#62B3ED" fontFamily="Moul" fontSize={18}>
						បញ្ជីរាយនាមមន្ត្រីដែលបានចូលរួមវគ្គបណ្តុះបណ្តាល និងសិក្ខាសាលា
					</Text>
				</Center>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex
							className="hidden-print responsive-header-for-table"
							bg="#FAFAFA"
							p="10px"
							pt="5px"
							pb="5px"
							mb="16px"
							alignItems="center"
							justifyContent="space-between"
							borderBottom="1px solid #dbdbdb"
						>
							<Flex justifyContent="flex-start">
								<Text alignItems="center" fontWeight="bold" fontSize="20px" pl={2}>
									{t('Training Course List')}
								</Text>
							</Flex>
							<Flex className="header-menu" alignItems="center" justifyContent="flex-end" minW="361px">
								<Button
									mr="2"
									onClick={() => window?.print()}
									leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
									colorScheme="gray"
									variant="solid"
									size="sm"
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
								>
									{t('Print')}
								</Button>
								<Button
									mr="2"
									onClick={() => {
										setExportRecords([]);
										modalExport?.onOpen();
									}}
									leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
									colorScheme="gray"
									variant="solid"
									size="sm"
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
								>
									{t('Export')}
								</Button>
								<Button
									onClick={modalFilter?.onOpen}
									leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
									colorScheme={`${no || organiser || courseTitle || department || officials || dateStart || dateEnd || trainingLocation || locationType || means || type || bioListing ? 'yellow' : 'gray'}`}
									variant="solid"
									size="sm"
									mr={user?.position !== '1' ? 2 : 0}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7}"
								>
									{t('Filter')}
								</Button>

								{canShowCreateButton && (
									<Button
										leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
										colorScheme="gray"
										variant="solid"
										size="sm"
										borderRadius={c.borderRadius}
										border="1px solid #bdc3c7"
										onClick={onOpen}
									>
										{t('Create')}
									</Button>
								)}
							</Flex>
						</Flex>

						<Box p="10px">
							<Table variant="simple" className="table-company-proposal-list responsive-table-container">
								<Thead>
									<Tr className="td-as-th">
										<Th align="left">{t('Number')}</Th>
										<Th align="left">{t('OfficialsName')}</Th>
										<Th align="left" style={{ width: '240px' }}>
											{t('Course Title')}
										</Th>
										<Th align="left">{t('Organiser')}</Th>
										<Th>{t('dateFrom')}</Th>
										<Th>{t('dateTo')}</Th>
										<Th align="left" style={{ width: '120px' }}>
											{t('location')}
										</Th>
										<Th align="center">{t('Means')}</Th>
										<Th align="center">{t('Type')}</Th>
										<Th align="right">{t('Document')}</Th>
										{canShowCreateButton && <Th align="right">{t('Actions')}</Th>}
									</Tr>
								</Thead>
								<Tbody>
									{loading && <LoadingResponsive column={9} />}
									{data?.getTrainingRecord?.records?.length <= 0 && (
										<Tr align="left">
											<Text ml={6} mt={2} mb={2}>
												{t('No Data')}
											</Text>
										</Tr>
									)}
									{record?.map((item, index) => {
										return (
											<Tr
												key={index}
												cursor="pointer"
												_hover={{ bg: 'gray.200' }}
												onClick={() => {
													openTrainingRecord(item);
													history.push(`/training-course/detail?id=${item.id}`);
												}}
												style={{
													cursor: 'pointer',
													backgroundColor: item?.notification?.read === false ? '#EEEEEE' : null,
												}}
											>
												<Td>{item?.no ? String(item?.no).padStart(4, '0') : 'N/A'}</Td>
												<Td>
													<Box width={showSmallScreen ? '150px' : '70px'}>
														{lang === 'kh' ? item?.user?.fullNameKhmer : item?.user?.fullName}
													</Box>
												</Td>
												<Td>
													<Box width={showSmallScreen ? '190px' : '100px'}>{item?.courseTitle}</Box>
												</Td>
												<Td>
													<Box width={showSmallScreen ? '150px' : '85px'}>{item?.organiser}</Box>
												</Td>
												<Td>
													<Box width="75px">{moment(item?.dateStart).format('LL')}</Box>
												</Td>
												<Td>
													<Box width="75px">{moment(item?.dateEnd).format('LL')}</Box>
												</Td>
												<Td>
													<Box width={showSmallScreen ? '150px' : '50px'}>{item?.location}</Box>
												</Td>
												<Td align="left">
													<Box width="50px">{t(item?.means)}</Box>
												</Td>
												<Td align="left">
													<Box>{t(item?.type)}</Box>
												</Td>
												<Td align="right">
													{item?.attachmentFile?.file?.length > 0 ? (
														<Flex className="viewContainer" justifyContent="end" onClick={(e) => e.stopPropagation()}>
															<SmallDownloadAndViewButton url={item?.attachmentFile?.file} marginLeft={0} />
														</Flex>
													) : (
														<Box>
															<Text>N/A</Text>
														</Box>
													)}
												</Td>
												{canShowCreateButton && <Td align="right">{actionMenu(item)}</Td>}
											</Tr>
										);
									})}
								</Tbody>
								<tfoot className="responsive-footer">
									<Tr className="td-as-th">
										<Th align="left">{t('Number')}</Th>
										<Th align="left">{t('OfficialsName')}</Th>
										<Th align="left">{t('Course Title')}</Th>
										<Th align="left">{t('Organiser')}</Th>
										<Th align="left" style={{ width: '240px' }}>
											{t('dateFrom')}
										</Th>
										<Th align="left" style={{ width: '290px' }}>
											{t('dateTo')}
										</Th>
										<Th align="left" style={{ width: '120px' }}>
											{t('location')}
										</Th>
										<Th align="center">{t('Means')}</Th>
										<Th align="center">{t('Type')}</Th>
										<Th align="right">{t('Document')}</Th>
										{canShowCreateButton && <Th align="right">{t('Actions')}</Th>}
									</Tr>
								</tfoot>
							</Table>
							<Flex justifyContent="flex-start" className="show-only-printing">
								{t('Total Rows')}: {data?.getTrainingRecord?.totalRows}
							</Flex>
							<Flex justifyContent="flex-end" className="hidden-print">
								{t('Total Rows')}: {data?.getTrainingRecord?.totalRows}
							</Flex>
							{data?.getTrainingRecord?.totalPages > 1 && (
								<Flex justifyContent="flex-end" w="100%" mt="4">
									<ReactPaginate
										previousLabel={<ChevronLeftIcon fontSize="20px" />}
										nextLabel={<ChevronRightIcon fontSize="20px" />}
										breakLabel={'...'}
										breakClassName={'break-me'}
										pageCount={data?.getTrainingRecord?.totalPages}
										marginPagesDisplayed={2}
										pageRangeDisplayed={3}
										onPageChange={handlePageClick}
										containerClassName={'pagination'}
										activeClassName={'active'}
										initialPage={(currentPage - 1) | 0}
									/>
								</Flex>
							)}
							<Flex className="hidden-print" justifyContent="flex-end" mt="6">
								<Text mr="2">{t('Show more rows')}</Text>
								<Checkbox
									isChecked={rowLimit > 10}
									onChange={() => {
										handlePageClick({ selected: 0 });
										if (rowLimit > 10) {
											setRowLimit(10);
										} else {
											setRowLimit(300);
										}
									}}
								/>
							</Flex>
						</Box>
					</Box>
				</Center>
			</Container>

			<Modal size="xl" isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pt={2}>
					<ModalHeader>{t('Create a Training Course')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Flex justifyContent="center" pb={12}>
							<TrainingRecordAdd
								onCloseModal={() => {
									onClose();
									loadData({ variables: getVariables() });
								}}
							/>
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			<AlertDialog isOpen={isOpeningDeleteDialog} leastDestructiveRef={deleteDialogRef} onClose={onRejectDeleteDialog}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Delete Training Course')}
						</AlertDialogHeader>

						<AlertDialogBody>
							<Text fontSize="15px">{t("Are you sure? You can't undo this action afterwards")}</Text>
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} isLoading={loadingRemoveTrainingCourse} ref={deleteDialogCancelRef} onClick={onRejectDeleteDialog}>
								{t('Cancel')}
							</Button>
							<Button isLoading={loadingRemoveTrainingCourse} colorScheme="red" onClick={onApproveDeleteDialog}>
								{t('Delete')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>

			<TrainingRecordExport isOpen={modalExport?.isOpen} onClose={modalExport?.onClose} />

			<TrainingCourseFilter
				callback={callbackFilter}
				isOpen={modalFilter?.isOpen}
				onClose={modalFilter?.onClose}
				officials={officials}
				officialName={officialName}
				officialNameKhmer={officialNameKhmer}
				officialAvatar={officialAvatar}
				no={no}
				organiser={organiser}
				trainingLocation={trainingLocation}
				locationType={locationType}
				type={type}
				means={means}
				dateStart={dateStart}
				dateEnd={dateEnd}
				courseTitle={courseTitle}
				department={department}
				office={office}
				read={read}
				bioListing={bioListing}
			/>
		</PrintLayout>
	);
}

export default TrainingRecordList;
